import { OPTIMIZED_TPV_CONFIG_KEYS } from '../core/constants';

/**
 * Utility functions for validating Optimized TPV Requests configuration.
 * Ensures required dependencies (like Vast Daisy Chaining) are properly configured.
 */

/**
 * Check if Optimized TPV Requests is enabled
 * @param {Object} rawConfig - The flattened config object
 * @returns {boolean} - True if optimized TPV is enabled
 */
export const isOptimizedTpvEnabled = (rawConfig) => {
    return !!rawConfig?.[OPTIMIZED_TPV_CONFIG_KEYS.OPTIMIZED_TPV_ENABLED];
};

/**
 * Get the Vast Daisy Chaining config object
 * @param {Object} rawConfig - The flattened config object
 * @returns {Object|null} - The vast daisy chaining config or null
 */
export const getVastDaisyChainingConfig = (rawConfig) => {
    return rawConfig?.[OPTIMIZED_TPV_CONFIG_KEYS.VAST_DAISY_CHAINING_CONFIG] || null;
};

/**
 * Validate Vast Daisy Chaining configuration for Optimized TPV
 * Returns validation status and detailed message
 * @param {Object} rawConfig - The flattened config object
 * @returns {Object} - Validation result with isValid and message
 */
export const validateVastDaisyChainingForOptimizedTpv = (rawConfig) => {
    const vastDaisyChainingConfig = getVastDaisyChainingConfig(rawConfig);

    if (!vastDaisyChainingConfig || Object.keys(vastDaisyChainingConfig).length === 0) {
        return {
            isValid: false,
            message: 'Vast Daisy Chaining is not configured.'
        };
    }

    const entries = Object.entries(vastDaisyChainingConfig);

    const hasValidEntry = entries.some(([_, config]) => {
        return config?.enabled === true &&
            Array.isArray(config?.vastTags) &&
            config.vastTags.length > 0;
    });

    if (!hasValidEntry) {
        return {
            isValid: false,
            message: 'At least one country (or DEFAULT) must be enabled and have VastTags configured.'
        };
    }

    return {
        isValid: true,
        message: 'Vast Daisy Chaining is properly configured.'
    };
};

/**
 * Validate if Optimized TPV can be enabled
 * Validates Vast Daisy Chaining configuration
 * @param {Object} rawConfig - The flattened config object
 * @returns {Object} - Validation result with canEnable, errors array
 */
export const canEnableOptimizedTpv = (rawConfig) => {
    const errors = [];

    const vastValidation = validateVastDaisyChainingForOptimizedTpv(rawConfig);
    if (!vastValidation.isValid) {
        errors.push({
            type: 'VAST_DAISY_CHAINING_INVALID',
            message: `Cannot enable Vast Broadcasting: ${vastValidation.message}`
        });
    }

    return {
        canEnable: errors.length === 0,
        errors,
        vastDaisyChainingValidation: vastValidation
    };
};
